/*
 * carousel — Level 2 (CSP build) progressive enhancement over a native CSS
 * scroll-snap track.
 *
 * Without JS the track is already a horizontally scrollable, snapping row of
 * slides (see resources/css/components/_carousel.css). This component only adds
 * prev/next buttons and a slide counter, and it derives ALL of its state —
 * current slide, edge-disabled — PURELY from the track's scroll position. There
 * is no JS-authored slide model to drift out of sync with the DOM.
 *
 * Compositor discipline: layout is read (offsetLeft / clientWidth / scrollWidth)
 * ONCE in measure() on init + resize and cached. During scroll only the cheap
 * scrollLeft is read, and that read is rAF-throttled so a burst of scroll events
 * can't thrash layout. Nothing here reads layout inside the scroll handler.
 *
 * Registered in resources/js/app.js as: Alpine.data('carousel', carousel)
 * Paired with resources/views/components/interactive/carousel.antlers.html.
 */
export default function carousel() {
    return {
        atStart: true,
        atEnd: false,
        index: 0,
        total: 0,

        // Cached layout (set in measure(); never read during scroll).
        _slideStep: 0,
        _maxScroll: 0,
        _rafId: null,
        _reduceMotion: false,
        _onResize: null,

        // Drag-to-scroll state. All of it is set on pointerdown and read during
        // the drag; nothing here is layout.
        _dragging: false,
        _dragStartX: 0,
        _dragStartScroll: 0,
        _dragMoved: false,
        _dragListeners: null,

        init() {
            const track = this.$refs.track;
            if (!track) {
                return;
            }

            this._reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            this.measure();
            this.updateFromScroll();

            // Slide widths change on resize — re-measure then (never during scroll).
            this._onResize = () => {
                this.measure();
                this.updateFromScroll();
            };
            window.addEventListener('resize', this._onResize, { passive: true });

            this.initDrag(track);
        },

        /*
         * CLICK AND DRAG, MOUSE ONLY.
         *
         * Touch already works and always has: the viewport is a native
         * scroll-snap container, so a finger scrolls it with no JS at all. A
         * MOUSE does not — browsers give an overflow container a scrollbar and
         * a wheel, and nothing else — so this fills that gap and only that gap.
         * `pointerType !== 'mouse'` returns early rather than trying to improve
         * on the native touch behaviour, which is smoother than anything driven
         * from JS and already has momentum and rubber-banding.
         *
         * NOT PointerTracker. That module returns a no-op instance the moment
         * `navigator.maxTouchPoints === 0`, i.e. exactly the devices this is
         * for, and its recognizers are built for swipe/pan/pinch on touch. This
         * is eight lines of scrollLeft arithmetic; a recognizer would be a
         * heavier answer to a smaller question.
         *
         * Listeners are attached HERE rather than as @pointerdown in the
         * template: the CSP Alpine build parses template expressions with a
         * hand-rolled grammar, and keeping a gesture entirely in JS is one less
         * thing that can silently no-op there. It also means the kit's own
         * carousel gets the behaviour without touching its markup.
         */
        initDrag(track) {
            const onDown = (e) => {
                // Mouse only, primary button only. `e.button === 0` also rules
                // out a middle-click, which otherwise starts autoscroll AND a
                // drag at the same time.
                if (e.pointerType !== 'mouse' || e.button !== 0) {
                    return;
                }

                this._dragging = true;
                this._dragMoved = false;
                this._dragStartX = e.clientX;
                // Read once, at the start of the gesture — the cached origin the
                // whole drag is measured against, per the compositor rule.
                this._dragStartScroll = track.scrollLeft;

                // Snap fights a per-frame scrollLeft write: with `x mandatory`
                // the browser keeps pulling the track back to the nearest slide
                // mid-drag and the card judders under the cursor. Off during the
                // gesture, restored on release — where snapping is exactly what
                // should happen, and does, to whatever slide you let go nearest.
                track.style.scrollSnapType = 'none';
                track.classList.add('is-dragging');
            };

            const onMove = (e) => {
                if (!this._dragging) {
                    return;
                }

                const dx = e.clientX - this._dragStartX;

                // 4px of slop before a press becomes a drag. Below it this is
                // still a click on a card, and the card is a link.
                if (!this._dragMoved && Math.abs(dx) > 4) {
                    this._dragMoved = true;
                    // Only capture once we know it is a drag; capturing on
                    // pointerdown would swallow the click on a card.
                    if (track.setPointerCapture) {
                        try {
                            track.setPointerCapture(e.pointerId);
                        } catch {
                            // Pointer already released — nothing to capture.
                        }
                    }
                }

                if (this._dragMoved) {
                    // Drag left, scroll right: the content follows the cursor.
                    track.scrollLeft = this._dragStartScroll - dx;
                }
            };

            const onUp = () => {
                if (!this._dragging) {
                    return;
                }
                this._dragging = false;
                track.style.scrollSnapType = '';
                track.classList.remove('is-dragging');
            };

            // THE CLICK AFTER A DRAG MUST NOT NAVIGATE. Every case-study card is
            // an <a> wrapping the whole card, so releasing the mouse over one
            // fires a click and the browser follows it — you drag the rail and
            // land on a case study you were only scrolling past. Capture phase,
            // so it runs before the link's own handling.
            const onClickCapture = (e) => {
                if (this._dragMoved) {
                    e.preventDefault();
                    e.stopPropagation();
                    this._dragMoved = false;
                }
            };

            // Images and links are natively draggable; without this the browser
            // starts its own drag-and-drop and the ghost image follows the
            // cursor instead of the track scrolling.
            const onDragStart = (e) => {
                if (this._dragging) {
                    e.preventDefault();
                }
            };

            track.addEventListener('pointerdown', onDown);
            track.addEventListener('pointermove', onMove);
            track.addEventListener('pointerup', onUp);
            track.addEventListener('pointercancel', onUp);
            track.addEventListener('click', onClickCapture, true);
            track.addEventListener('dragstart', onDragStart);
            // Releasing outside the track still ends the gesture.
            window.addEventListener('pointerup', onUp);

            this._dragListeners = { track, onDown, onMove, onUp, onClickCapture, onDragStart };
        },

        /**
         * Cache the slide step (distance between consecutive slide starts) and the
         * maximum scroll offset. Reads layout — only ever called on init + resize.
         */
        measure() {
            const track = this.$refs.track;
            if (!track) {
                return;
            }

            const slides = Array.from(track.querySelectorAll('[data-carousel-slide]'));
            this.total = slides.length;

            if (slides.length >= 2) {
                this._slideStep = slides[1].offsetLeft - slides[0].offsetLeft;
            } else if (slides.length === 1) {
                this._slideStep = slides[0].offsetWidth;
            } else {
                this._slideStep = track.clientWidth;
            }

            this._maxScroll = track.scrollWidth - track.clientWidth;
        },

        /**
         * Scroll handler bound via @scroll.passive. rAF-throttled: at most one
         * scrollLeft read per frame, no other layout reads.
         */
        onScroll() {
            if (this._rafId !== null) {
                return;
            }
            this._rafId = requestAnimationFrame(() => {
                this._rafId = null;
                this.updateFromScroll();
            });
        },

        /**
         * Derive current index + edge-disabled state from scrollLeft alone, using
         * the cached dimensions. A 1px tolerance absorbs sub-pixel rounding.
         */
        updateFromScroll() {
            const track = this.$refs.track;
            if (!track) {
                return;
            }

            const x = track.scrollLeft;
            // Index first, then derive atStart from it: rounding to the nearest slide
            // absorbs any leading offset (e.g. track padding) that would otherwise keep
            // scrollLeft a few px above 0 at rest and mis-read the first slide as scrolled.
            this.index = this._slideStep > 0 ? Math.round(x / this._slideStep) : 0;
            this.atStart = this.index <= 0;
            this.atEnd = x >= this._maxScroll - 1;
        },

        next() {
            this.scrollByStep(1);
        },

        prev() {
            this.scrollByStep(-1);
        },

        scrollByStep(direction) {
            const track = this.$refs.track;
            if (!track || this._slideStep <= 0) {
                return;
            }
            track.scrollBy({
                left: direction * this._slideStep,
                behavior: this._reduceMotion ? 'auto' : 'smooth',
            });
        },

        get counterLabel() {
            if (this.total === 0) {
                return '';
            }
            return `${Math.min(this.index + 1, this.total)} / ${this.total}`;
        },

        destroy() {
            if (this._rafId !== null) {
                cancelAnimationFrame(this._rafId);
                this._rafId = null;
            }
            if (this._onResize) {
                window.removeEventListener('resize', this._onResize);
                this._onResize = null;
            }
            // Swup replaces #swup and Alpine.destroyTree() runs on the old DOM,
            // so this fires on every soft navigation. The window listener is the
            // one that would otherwise accumulate — the track's own go with the
            // element, but window's does not.
            if (this._dragListeners) {
                const { track, onDown, onMove, onUp, onClickCapture, onDragStart } = this._dragListeners;
                track.removeEventListener('pointerdown', onDown);
                track.removeEventListener('pointermove', onMove);
                track.removeEventListener('pointerup', onUp);
                track.removeEventListener('pointercancel', onUp);
                track.removeEventListener('click', onClickCapture, true);
                track.removeEventListener('dragstart', onDragStart);
                window.removeEventListener('pointerup', onUp);
                this._dragListeners = null;
            }
        },
    };
}
